import React, { useRef } from 'react';
import { LazyRender, MembershipBanner } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import { BasketErrorNotification } from 'src/components/atoms/BasketErrorNotification/BasketErrorNotification';
import { PARTNER_MODULE_ID } from 'src/components/layouts/PDP/elementIds';
import { BasketCTAButton } from 'src/components/molecules/BasketCTAButton/BasketCTAButton';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { useBasketCTAButton } from 'src/components/molecules/BasketCTAButton/useBasketCTAButton';
import { DeliverySummary } from 'src/components/molecules/DeliverySummary/DeliverySummary';
import { useDeliverySummary } from 'src/components/molecules/DeliverySummary/useDeliverySummary';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { PublicAttributes } from 'src/components/molecules/PublicAttributes/PublicAttributes';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustBannerWithTracking } from 'src/components/molecules/TrustBanner/components/TrustBannerWithTracking';
import { CustomersReviewsPreview } from 'src/components/organisms/CustomersReviewsPreview/components/CustomersReviewsPreview';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductInformation } from 'src/components/organisms/ProductInformation/components/ProductInformation';
import {
  PRODUCT_MEDIA_SECTION_LABEL_PLURAL,
  PRODUCT_MEDIA_SECTION_LABEL_SINGLE,
} from 'src/components/organisms/ProductMediaCarousel/constants/copy';
import { ProductMediaCarousel } from 'src/components/organisms/ProductMediaCarousel/ProductMediaCarousel';
import { useProductMediaCarouselActions } from 'src/components/organisms/ProductMediaCarousel/useProductMediaCarouselActions';
import { useProductMediaModalActions } from 'src/components/organisms/ProductMediaModal/useProductMediaModalActions';
import { ProductOptions } from 'src/components/organisms/ProductOptions/ProductOptions';
import { useProductOptions } from 'src/components/organisms/ProductOptions/useProductOptions';
import { RecommendedProductsCarouselSection } from 'src/components/organisms/RecommendedProductsCarousel/RecommendedProductsCarouselSection';
import { ReviewsSummary } from 'src/components/organisms/ReviewsSummary/components/ReviewsSummary';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { trackBreadcrumbClick } from 'src/tracking/trackBreadcrumbClick';
import { trackProductInformationToggle } from 'src/tracking/trackProductInformationToggle';
import { trackProductInformationVisible } from 'src/tracking/trackProductInformationVisible';
import { trackPublicAttributesToggle } from 'src/tracking/trackPublicAttributesToggle';
import { useEditProductOptions } from '../hooks/useEditProductOptions';
import { usePDPViewModel } from '../hooks/usePDPViewModel';
import { PDPLayoutView } from './PDPLayoutView';
import * as styles from './PDPLayoutView.styles';

// Opened by a URL fragment the container reads on mount, so a page loaded with it still
// opens the drawer once the chunk arrives. Never open on the server, so nothing to render there.
const CustomersReviewsDrawerContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "customers_reviews_drawer" */ 'src/components/organisms/CustomersReviewsDrawer/containers/CustomersReviewsDrawerContainer'
    ).then((mod) => mod.CustomersReviewsDrawerContainer),
  { ssr: false },
);

const DeliveryDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_drawer" */ 'src/components/organisms/DeliveryDrawer/DeliveryDrawer'
  ).then((mod) => mod.DeliveryDrawer),
);

// The express wallets are client-only (they interrogate the browser/OS for wallet availability) and
// pull in the Adyen SDK, so they are kept out of the main PDP bundle and off the server render. The
// block renders nothing until both wallets report availability, so there is no layout shift cost to
// loading it late.
const ExpressCheckoutContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "express_checkout" */ 'src/components/organisms/ExpressCheckout/ExpressCheckoutContainer'
    ).then((mod) => mod.ExpressCheckoutContainer),
  { ssr: false },
);

const ProductOptionsDrawerContainer = dynamic(() =>
  import(
    /* webpackChunkName: "product_options_drawer" */ 'src/components/organisms/ProductOptions/ProductOptionsDrawerContainer'
  ).then((mod) => mod.ProductOptionsDrawerContainer),
);

/*
  The full-screen modal renders nothing until isModalOpen flips, so it and the thumbnail
  rail it owns are split out of the page bundle. (react-modal itself is not saved: packages
  _app already loads pull it into the shared chunk either way.)

  Rendered unconditionally rather than gated on isModalOpen, which is deliberate: the chunk
  then loads just after hydration instead of on the tap that opens the modal, so opening
  stays instant, and react-modal keeps the mounted component it needs to animate its close
  - closeTimeoutMS outlives the state change, so unmounting on close would cut it short.
*/
const ProductMediaModal = dynamic(() =>
  import(
    /* webpackChunkName: "product_media_modal" */ 'src/components/organisms/ProductMediaModal/ProductMediaModal'
  ).then((mod) => mod.ProductMediaModal),
);

const PartnerMaxiModule = dynamic(() =>
  import(
    /* webpackChunkName: "partner_maxi_module" */ 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModule'
  ).then((mod) => mod.PartnerMaxiModule),
);

const PartnerMiniModule = dynamic(() =>
  import(
    /* webpackChunkName: "partner_mini_module" */ 'src/components/molecules/PartnerMiniModule/PartnerMiniModule'
  ).then((mod) => mod.PartnerMiniModule),
);

const PlpPreviewCarouselContainer = dynamic(() =>
  import('src/components/organisms/PlpPreviewCarousel/containers/PlpPreviewCarouselContainer').then(
    (mod) => mod.PlpPreviewCarouselContainer,
  ),
);

const LAZY_LOAD_MARGIN = '200px';
const PUBLIC_ATTRIBUTES_EXPANDABLE_THRESHOLD = 6;

export const PDPLayout = () => {
  const {
    breadcrumbs,
    customersReviewsPreview,
    deliveryOptionsLoaded,
    hasProductOptions,
    linkCarousel,
    media,
    mediaModal,
    membershipBanner,
    pageLiveRegionText,
    partner,
    plpPreview,
    productFeatures,
    productInformation,
    productSummary,
    publicAttributes,
    recommendedProducts,
    reviewsSummary,
    similarProductsServingConfig,
    trustBanner,
  } = usePDPViewModel();
  const deliverySummaryProps = useDeliverySummary();
  const productOptionsProps = useProductOptions();
  const basketCtaProps = useBasketCTAButton();
  const mediaCarouselActions = useProductMediaCarouselActions();
  const mediaModalActions = useProductMediaModalActions();
  const rightColumnRef = useRef<HTMLDivElement>(null);

  useEditProductOptions();

  return (
    <>
      <PDPLayoutView
        breadcrumbs={breadcrumbs}
        ctaColumnRef={rightColumnRef}
        ctaTrigger={
          <>
            {deliveryOptionsLoaded && <DeliverySummary {...deliverySummaryProps} />}
            <ProductOptions {...productOptionsProps} />
            <BasketCTAButton {...basketCtaProps} />
            <ExpressCheckoutContainer />
          </>
        }
        linkCarousel={linkCarousel && <LinkCarouselContainer {...linkCarousel} />}
        media={
          <ProductMediaCarousel
            {...media}
            {...mediaCarouselActions}
            // The gallery sits in the page's images column, so it becomes a grid of tiles
            // once the page has two columns. The stylesheet decides when.
            allowGridLayout
            location="Main media"
            mobileEdgeToEdge
            sectionLabel={
              media.media.length > 1
                ? PRODUCT_MEDIA_SECTION_LABEL_PLURAL
                : PRODUCT_MEDIA_SECTION_LABEL_SINGLE
            }
            showDots
            showFavouriteButton
            showNumberOfSlides
            // While the modal owns navigation the carousel is covered; it re-syncs on close
            suspendScrollSync={mediaModal.isModalOpen}
          />
        }
        membershipBanner={membershipBanner && <MembershipBanner {...membershipBanner} />}
        onBreadcrumbLinkClick={trackBreadcrumbClick}
        pageLiveRegionText={pageLiveRegionText}
        partnerMaxiModule={
          <div id={PARTNER_MODULE_ID}>
            <LazyRender options={{ rootMargin: LAZY_LOAD_MARGIN }}>
              <PartnerMaxiModule
                dateJoined={partner.dateJoined}
                isTopBrand={partner.isTopBrand}
                location={partner.location}
                logoSrc={partner.logoSrc}
                name={partner.name}
                numberOfOrders={partner.numberOfOrders}
                onContactPartnerClick={partner.onContactPartnerClick}
                onStoryClick={partner.onStoryClick}
                onVisible={partner.onImpression}
                onVisitShopClick={partner.onVisitShopClick}
                partnerLinkHref={partner.storefrontHref}
                rating={partner.rating}
                storyImages={partner.storyImages}
                storySummary={partner.storySummary}
                storyText={partner.storyText}
              />
            </LazyRender>
          </div>
        }
        partnerMiniModule={
          <LazyRender
            options={{ rootMargin: LAZY_LOAD_MARGIN }}
            placeholder={<div css={styles.partnerMiniModulePlaceholder} />}
          >
            <PartnerMiniModule
              dateJoined={partner.dateJoined}
              isLoading={!partner.isDataLoaded}
              isTopBrand={partner.isTopBrand}
              linkHref={partner.anchorHref}
              location={partner.location}
              logoSrc={partner.logoSrc}
              name={partner.name}
              numberOfOrders={partner.numberOfOrders}
              rating={partner.rating}
            />
          </LazyRender>
        }
        partnerRecommendedProducts={
          <RecommendedProductsCarouselSection
            {...recommendedProducts}
            heading="Our bestsellers"
            servingConfig="pdp_same_seller_2"
          />
        }
        plpPreview={
          plpPreview && (
            <LazyRender options={{ rootMargin: LAZY_LOAD_MARGIN }}>
              <PlpPreviewCarouselContainer {...plpPreview} />
            </LazyRender>
          )
        }
        productFeatures={productFeatures}
        productInformation={
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={trackProductInformationToggle}
          >
            <ProductInformation
              {...productInformation}
              onVisible={trackProductInformationVisible}
            />
          </ExpandablePanel>
        }
        productSummary={productSummary}
        publicAttributes={
          publicAttributes.length > PUBLIC_ATTRIBUTES_EXPANDABLE_THRESHOLD ? (
            <ExpandablePanel
              collapseButtonText="Read less"
              expandButtonText="Read more"
              onToggle={trackPublicAttributesToggle}
            >
              <PublicAttributes publicAttributes={publicAttributes} />
            </ExpandablePanel>
          ) : (
            <PublicAttributes publicAttributes={publicAttributes} />
          )
        }
        recommendedProducts={
          <RecommendedProductsCarouselSection
            {...recommendedProducts}
            heading="Similar products"
            servingConfig={similarProductsServingConfig}
          />
        }
        reviews={
          <div id={REVIEWS_URL_FRAGMENT_ID}>
            {reviewsSummary && (
              <div css={styles.reviewsSummaryWrapper}>
                <ReviewsSummary {...reviewsSummary} />
              </div>
            )}
            <div css={styles.productReviewsWrapper}>
              <CustomersReviewsPreview {...customersReviewsPreview} />
            </div>
          </div>
        }
        stickyCta={
          <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
            <BasketCTAButton {...basketCtaProps} />
          </StickySection>
        }
        trustBanner={trustBanner && <TrustBannerWithTracking {...trustBanner} />}
      />
      <ProductMediaModal {...mediaModal} {...mediaModalActions} />
      {/* Beside the other drawers, not inside the reviews section: it is full-screen and
          URL-driven, and nesting it would put it in that section's stacking and lazy-render context. */}
      <CustomersReviewsDrawerContainer target={rightColumnRef} />
      <DeliveryDrawer target={rightColumnRef} />
      {hasProductOptions && <ProductOptionsDrawerContainer target={rightColumnRef} />}
      <BasketErrorNotification />
    </>
  );
};
